import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CompareDateResultResponse,
  CompareRecordsResponse,
  CompareTestRecordsPayload,
  RunTestResultResponse,
  RunTestsBody,
  GetSingleTestDataResponse,
  GetSingleTestDataPayload,
  testUrls
} from '../interfaces/test.interface';
import { createHeaders } from '../utils/createHeaders';
import { baseQueryWithReAuth } from './api';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/athena`;

export const apiTests = createApi({
  reducerPath: 'apiTests',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['RunTests', 'CompareTest', 'GetSingleTest', 'CompareTestRecords', 'SingleTestData', 'PendingTestsCount'],
  endpoints: (build) => ({
    runTests: build.mutation<RunTestResultResponse, RunTestsBody>({
      query: (data) => ({
        url: '/testautomationrequest',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['RunTests']
    }),
    getCompareTestRecords: build.query<CompareRecordsResponse, CompareTestRecordsPayload>({
      query: (data) => ({
        url: '/results/flag/compare',
        method: 'POST',
        body: data
      }),
      providesTags: ['CompareTestRecords']
    }),
    getCompareTestData: build.query<CompareDateResultResponse, string>({
      query: (id) => ({
        url: `/results/flag/compare/${id}`,
        method: 'GET'
      }),
      providesTags: ['CompareTest']
    }),
    getSingleTestData: build.query<GetSingleTestDataResponse, GetSingleTestDataPayload>({
      query: ({ requester, dateTime, id, jiraTicketId, authKey }) => {
        const headers = createHeaders(authKey);
        return {
          url: `/results/test`,
          method: 'GET',
          params: { requester, dateTime, id, jiraTicketId },
          headers
        };
      },
      providesTags: ['SingleTestData']
    }),
    getTestUrls: build.query<{ testUrls: testUrls[] }, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/results/testUrls/${id}`,
          method: 'GET'
        };
      },
      providesTags: ['SingleTestData']
    }),
    getPendingTests: build.query<{ count: string }, void>({
      query: () => ({
        url: '/pending-tests-count',
        method: 'GET'
      }),
      providesTags: ['PendingTestsCount']
    })
  })
});

export const {
  useRunTestsMutation,
  useGetSingleTestDataQuery,
  useGetCompareTestRecordsQuery,
  useGetCompareTestDataQuery,
  useLazyGetTestUrlsQuery,
  useGetPendingTestsQuery
} = apiTests;
